// ============================================================
// ALL OPTIMIZATIONS INLINE — SINGLE FILE
// 1.  Memoized SchemeRow / SkuRow — only toggled row re-renders
// 2.  Batched setState — one call per toggle
// 3.  Debounced updatePromotionName (150ms)
// 4.  No state mutation — spread before delete
// 5.  jQuery DataTables REMOVED — pure React search+pagination
//     (DataTables was the #1 cause of "Choose Schemes" lag:
//      _initSkuTable fired on every componentDidUpdate, its
//      setTimeout retry loop kept re-running, and destroy()
//      blocked the JS thread before the tab could render)
// 6.  No duplicate updatePromotionName calls
// 7.  Counters derived in render, not stored in state
// 8.  Parallel API calls in componentDidMount
// 9.  Memoized promotion-name sub-computations (_cache{})
// 10. Tab content lazy-rendered via _visitedTabs Set
// 11. React search + pagination for SKU table (debounced 250ms)
// 12. React search + pagination for Scheme table (debounced 250ms)
// ============================================================

import { Tab, TabPanel, Tabs, TabList } from "react-web-tabs";
import "react-web-tabs/dist/react-web-tabs.css";
import config from 'react-global-configuration';
import { Link } from "react-router-dom";
import React, { Component, memo, useCallback } from 'react';
import axios from "axios";
import { Button, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import Checkbox from "components/CustomCheckbox/CustomCheckbox.jsx";
import Select from 'react-select';
import Switch from 'react-switch';
import { css } from '@emotion/core';
import { BeatLoader } from 'react-spinners';
import NotificationSystem from "react-notification-system";
import { style } from "variables/Variables.jsx";
import '../assets/css/react-confirm-alert/react-confirm-alert.css';
import ReactTooltip from 'react-tooltip';
import jwt_decode from "jwt-decode";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SimpleReactValidator from 'simple-react-validator';
import AsyncPaginate, { reduceGroupedOptions } from 'react-select-async-paginate';
import moment from 'moment';
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import loadOptions from './loadOptions';
import CommonNavbar from "./commonNavbar";
import { List } from "react-virtualized";
import debounce from 'lodash/debounce';

// ─────────────────────────────────────────────────────────────
// WHY "Choose Schemes" was slow — root causes in old code:
//
//  1. componentDidUpdate called _initSkuTable() on EVERY state
//     change (including the setState inside openSectionToBanks).
//     That function called DataTable().destroy() + DataTable()
//     init synchronously, blocking the thread.
//
//  2. _initSkuTable had a setTimeout retry loop — if the DOM
//     element wasn't found it kept scheduling itself, firing
//     multiple overlapping inits.
//
//  3. DataTables conflicts with React memo rows: after toggling
//     a switch, DataTables re-scanned the entire DOM, fighting
//     React's virtual DOM and causing reconciliation thrashing.
//
//  FIX: Remove jQuery DataTables entirely. Use pure React state
//  for search (debounced) and pagination (slice). Zero DOM
//  contention, zero setTimeout races, instant tab switch.
// ─────────────────────────────────────────────────────────────

// ── Inject table control CSS once at module level ────────────
// const _TBL_STYLE_ID = 'react-tbl-ctrl-css';
// if (typeof document !== 'undefined' && !document.getElementById(_TBL_STYLE_ID)) {
//   const s = document.createElement('style');
//   s.id = _TBL_STYLE_ID;
//   s.textContent = `
//     .rtbl-bar{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;margin:8px 4px}
//     .rtbl-bar-left{display:flex;align-items:center;gap:6px}
//     .rtbl-bar-right{display:flex;align-items:center;gap:6px}
//     .rtbl-info{font-size:12px;color:#666;white-space:nowrap}
//     .rtbl-size-select{border:1px solid #ccc;border-radius:4px;padding:3px 6px;font-size:13px;cursor:pointer}
//     .rtbl-search{border:1px solid #ccc;border-radius:4px;padding:4px 10px;font-size:13px;width:220px;outline:none;transition:border-color .2s}
//     .rtbl-search:focus{border-color:#484b9a}
//     .rtbl-footer{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;margin:8px 4px}
//     .rtbl-pages{display:flex;align-items:center;gap:3px;flex-wrap:wrap}
//     .rtbl-pg-btn{min-width:30px;height:28px;padding:0 7px;border:1px solid #ccc;border-radius:4px;background:#fff;font-size:12px;cursor:pointer;transition:background .15s,border-color .15s,color .15s;line-height:26px;text-align:center}
//     .rtbl-pg-btn:hover:not(:disabled){background:#f0f0f7;border-color:#484b9a}
//     .rtbl-pg-btn.active{background:#484b9a;color:#fff;border-color:#484b9a;font-weight:600}
//     .rtbl-pg-btn:disabled{opacity:.4;cursor:not-allowed}
//     .rtbl-ellipsis{font-size:12px;color:#888;padding:0 3px;line-height:28px}
//   `;
//   document.head.appendChild(s);
// }

// Replace the style injection with this updated version
const _TBL_STYLE_ID = 'react-tbl-ctrl-css';
if (typeof document !== 'undefined' && !document.getElementById(_TBL_STYLE_ID)) {
  const s = document.createElement('style');
  s.id = _TBL_STYLE_ID;
  s.textContent = `
    .rtbl-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
      margin: 12px 4px 8px 4px;
      padding: 0 8px;
    }
    .rtbl-bar-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .rtbl-bar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .rtbl-info {
      font-size: 13px;
      color: #555;
      white-space: nowrap;
    }
    .rtbl-size-select {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 13px;
      cursor: pointer;
      background: #fff;
    }
    .rtbl-search {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 6px 12px;
      font-size: 13px;
      width: 260px;
      outline: none;
      transition: border-color 0.2s;
    }
    .rtbl-search:focus {
      border-color: #484b9a;
      box-shadow: 0 0 0 2px rgba(72, 75, 154, 0.1);
    }
    .rtbl-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
      margin: 12px 4px;
      padding: 0 8px;
    }
    .rtbl-pages {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-wrap: wrap;
    }
    .rtbl-pg-btn {
      min-width: 32px;
      height: 32px;
      padding: 0 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fff;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s;
      line-height: 30px;
      text-align: center;
    }
    .rtbl-pg-btn:hover:not(:disabled) {
      background: #f5f5f5;
      border-color: #484b9a;
      color: #484b9a;
    }
    .rtbl-pg-btn.active {
      background: #484b9a;
      color: #fff;
      border-color: #484b9a;
      font-weight: 500;
    }
    .rtbl-pg-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .rtbl-ellipsis {
      font-size: 13px;
      color: #888;
      padding: 0 4px;
      line-height: 32px;
    }
    
  `;
  document.head.appendChild(s);
}

// ── Reusable table toolbar (search + page-size) ──────────────
const TableControls = memo(({ searchValue, onSearch, pageSize, onPageSize }) => (
  <div className="rtbl-bar">
    <div className="rtbl-bar-left">
      <span className="rtbl-info">Show&nbsp;</span>
      <select className="rtbl-size-select" value={pageSize} onChange={e => onPageSize(Number(e.target.value))}>
        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <span className="rtbl-info">&nbsp;entries</span>
    </div>
    <div className="rtbl-bar-right">
      <span className="rtbl-info">Search:&nbsp;</span>
      <input
        className="rtbl-search"
        type="text"
        placeholder="Search…"
        value={searchValue}
        onChange={e => onSearch(e.target.value)}
      />
    </div>
  </div>
));

// ── Reusable table footer (info text + page buttons) ─────────
const TableFooter = memo(({ page, totalPages, totalRows, filteredRows, pageSize, onPage }) => {
  const start = filteredRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredRows);

  // Up to 5 page buttons around current, with ellipsis gaps
  const btns = [];
  const delta = 2;
  const L = Math.max(1, page - delta);
  const R = Math.min(totalPages, page + delta);
  if (L > 1) { btns.push(1); if (L > 2) btns.push('…'); }
  for (let i = L; i <= R; i++) btns.push(i);
  if (R < totalPages) { if (R < totalPages - 1) btns.push('…'); btns.push(totalPages); }

  return (
    <div className="rtbl-footer">
      <span className="rtbl-info">
        Showing {start}–{end} of {filteredRows} entries
        {filteredRows !== totalRows ? ` (filtered from ${totalRows} total)` : ''}
      </span>
      <div className="rtbl-pages">
        <button className="rtbl-pg-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>‹ Prev</button>
        {btns.map((b, i) =>
          b === '…'
            ? <span key={`e${i}`} className="rtbl-ellipsis">…</span>
            : <button key={b} className={`rtbl-pg-btn${b === page ? ' active' : ''}`} onClick={() => onPage(b)}>{b}</button>
        )}
        <button className="rtbl-pg-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next ›</button>
      </div>
    </div>
  );
});

// ── Memoized table rows ───────────────────────────────────────
// Custom comparator: re-render ONLY when isChecked changes.
// This is why toggling one row doesn't repaint the whole table.

const SchemeRow = memo(({ value, isChecked, onToggle, ts }) => {
  const handle = useCallback(
    (checked) => onToggle(value.id, value.schemeName, checked),
    [value.id, value.schemeName, onToggle]
  );
  return (
    <tr>
      <td>{value.schemeName}</td>
      <td>{value.bankName}</td>
      <td>{value.schemeType}</td>
      <td>{value.emiType}</td>
      <td>{value.tenure}</td>
      <td>{value.availCashback}</td>
      <td>{value.upfrontCashbackBrand}</td>
      <td>{value.upfrontCashbackBank}</td>
      <td>{value.postCashbackBrand}</td>
      <td>{value.postCashbackBank}</td>
      <td>{value.lowCostSubvention}</td>
      <td>{value.upfrontBrandSub}</td>
      <td>{value.upfrontBrandSubAmount}</td>
      <td>{value.upfrontMaxCashback}</td>
      <td>{value.postBrandSub}</td>
      <td>{value.postBrandSubAmount}</td>
      <td>{value.postBrandMaxAmount}</td>
      <td>{value.bankCbPer}</td>
      <td>{value.bankMaxCb}</td>
      <td>{value.tid}</td>
      <td>{ts}</td>
      <td>
        <Switch className="react-switch" height={20} width={38} onColor="#484b9a"
          onChange={handle} checked={isChecked} />
      </td>
    </tr>
  );
}, (p, n) => p.isChecked === n.isChecked && p.value.id === n.value.id);

const SkuRow = memo(({ value, rowIndex, isChecked, onToggle }) => {
  const handle = useCallback(
    (checked) => onToggle(value.brandSku, value.modelDisplayNumber, checked),
    [value.brandSku, value.modelDisplayNumber, onToggle]
  );
  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{value.manufacturerDisplayName}</td>
      <td>{value.categoryDisplayName}</td>
      <td>{value.modelDisplayNumber}</td>
      <td>{value.modelVersion}</td>
      <td>{value.modelDisplayNumber}</td>
      <td>{value.brandSku}</td>
      <td>{value.productMaxSellingPrice}</td>
      <td>{value.productMinSellingPrice}</td>
      <td>
        <Switch className="react-switch" height={20} width={38} onColor="#484b9a"
          onChange={handle} checked={isChecked} />
      </td>
    </tr>
  );
}, (p, n) => p.isChecked === n.isChecked && p.value.brandSku === n.value.brandSku);

// ── Pure filter helpers (outside class = never recreated) ─────
function filterSku(rows, q) {
  if (!q) return rows;
  const keywords = q.toLowerCase().trim().split(/\s+/).filter(k => k.length > 0);
  if (keywords.length === 0) return rows;
  
  return rows.filter(r => {
    const searchableFields = [
      r.manufacturerDisplayName, r.categoryDisplayName, r.modelDisplayNumber,
      r.modelVersion, r.brandSku, r.productMaxSellingPrice, r.productMinSellingPrice
    ].map(v => v != null ? String(v).toLowerCase() : '');
    
    // ALL keywords must match in at least ONE field
    return keywords.every(keyword =>
      searchableFields.some(field => field.includes(keyword))
    );
  });
}

function filterScheme(rows, q) {
  if (!q) return rows;
  const keywords = q.toLowerCase().trim().split(/\s+/).filter(k => k.length > 0);
  if (keywords.length === 0) return rows;
  
  return rows.filter(r => {
    const searchableFields = [
      r.schemeName, r.bankName, r.schemeType, r.emiType, r.tenure,
      r.availCashback, r.upfrontBrandSub, r.upfrontBrandSubAmount,
      r.postBrandSub, r.postBrandSubAmount, r.bankCbPer, r.bankMaxCb, r.tid
    ].map(v => v != null ? String(v).toLowerCase() : '');
    
    // ALL keywords must match in at least ONE field
    return keywords.every(keyword =>
      searchableFields.some(field => field.includes(keyword))
    );
  });
}

// ─────────────────────────────────────────────────────────────

const override = css`display:block;margin:0 auto;border-color:red;`;

class AdminCreatePromotion1 extends Component {

  constructor(props) {
    super(props);
    this._promotionNameCache = {};
    this._visitedTabs = new Set(['vertical-tab-zero']);

    this.state = {
      activer: { '0': false, '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false, '8': false, '9': false, '10': false, '11': false },
      isEMI: { '0': false, '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false, '8': false, '9': false, '10': false, '11': false },
      isUpfront: { '0': false, '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false, '8': false, '9': false, '10': false, '11': false },
      zeroCost: { '0': false, '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false, '8': false, '9': false, '10': false, '11': false },
      _notificationSystem: null,
      mapField: false,
      bank_name_list: ["AMEX BANK", "AXIS BANK", "CITIBANK", "HDFC Bank", "HSBC Bank", "ICICI Bank", "INDUSIND BANK", "KOTAK BANK", "RBL BANK", "SBI CARD", "STANDARD CHARTERED BANK", "YES BANK"],
      bankOptions: [
        { name: 'AMEX', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'Axis', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'Citi', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'HDFC', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'HSBC', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'ICICI', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'IndusInd', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'Kotak', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'RBL', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'SBI', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'SCB', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
        { name: 'YES', M3: true, M6: true, M9: true, M12: true, M18: true, M24: true },
      ],
      brandSubventionOptions: [{ value: 'Percent', label: 'Percent' }, { value: 'Cashback', label: 'Flat Cashback' }],
      bankSubventionOptions: [{ value: 'NA', label: 'NA' }, { value: 'Percent', label: 'Percent' }, { value: 'Cashback', label: 'Flat Cashback' }],
      merchantSubventionOptions: [
        { value: 'All_Level', label: 'Applicable Everywhere' }, { value: 'State_Level', label: 'State Level' },
        { value: 'City_Level', label: 'City Level' }, { value: 'Merchant_Level', label: 'Merchant Level' },
        { value: 'Chain_Level', label: 'Chain Level' }, { value: 'Store_Level', label: 'Store Level' },
      ],
      maxTxnAmount: 10000,
      selectedMerchantSubvention: 'All_Level',
      selectedState: [], selectedCity: [],
      merchant_disabler: true,
      brand_subvention_list: { '0': 'Percent', '1': 'Percent', '2': 'Percent', '3': 'Percent', '4': 'Percent', '5': 'Percent', '6': 'Percent', '7': 'Percent', '8': 'Percent', '9': 'Percent', '10': 'Percent', '11': 'Percent' },
      bank_subvention_list: { '0': 'NA', '1': 'NA', '2': 'NA', '3': 'NA', '4': 'NA', '5': 'NA', '6': 'NA', '7': 'NA', '8': 'NA', '9': 'NA', '10': 'NA', '11': 'NA' },
      brand_subvention_value: { '0': '10', '1': '10', '2': '10', '3': '10', '4': '10', '5': '10', '6': '10', '7': '10', '8': '10', '9': '10', '10': '10', '11': '10' },
      bank_subvention_value: { '0': '0', '1': '0', '2': '0', '3': '0', '4': '0', '5': '0', '6': '0', '7': '0', '8': '0', '9': '0', '10': '0', '11': '0' },
      maximum_cashback: { '0': '1000', '1': '1000', '2': '1000', '3': '1000', '4': '1000', '5': '1000', '6': '1000', '7': '1000', '8': '1000', '9': '1000', '10': '1000', '11': '1000' },
      disabler_list: [true, true, true, true, true, true, true, true, true, true, true, true],
      mapField2: false,
      startDate: moment(new Date()).format('YYYY-MM-DD'),
      endDate: moment(new Date()).format('YYYY-MM-DD'),
      sku_list: [], model_list: [], sku_list_name: [], model_list_name: [], scheme_list_name: [],
      raw_display_data: [], state_display_data: [], city_display_data: [],
      merchant_display_data: [], chain_display_data: [], store_display_data: [],
      state_data: [{ value: "NA", label: "NA" }], city_data: [{ value: "NA", label: "NA" }],
      selectedMerchValues: [], selectedRefineSubvention: [], slicedMerchValues: [],
      refineSearch: false,
      state_ida: '', city_ida: '', merchant_ida: '', chain_ida: '',
      refinerMerch: [false, false, false, false, false],
      mapField3: false, mapField0: false, brand_has_merchant: [],
      stepper: [false, true, true, true, true, true],
      current_tab: 'vertical-tab-zero',
      campaign_name: 'Promotion name will be auto-generated',
      campaign_desc: '',
      merchant_length: null, merchant_counter: 1, merchant_counter_array: [0],
      follower: false,
      unmappedModel: false, data: [], loading: true, versions: [], tableLists: [],
      deleteKeys: [], map_status: null, stored_file_name: null, file_name: null,
      userGroup: null, id: null, campaign_response: [],
      activer2: { '0': true, '1': true, '2': true, '3': true, '4': true, '5': true, '6': true, '7': true, '8': true, '9': true, '10': true, '11': true },
      sku_list1: [], is_download: false, uploadDropdown: false, merchant2_display_data: [],
      user_id: null, merchant_file_name: null, merchant3_data: [],
      isSubmit: false, selectedFile: null, brand_file_uploaded: [], timer: null,
      scheme_master_data: [], scheme_master_list: [], scheme_master_name: [],
      scheme_mapping_data: [], scheme_mapping_file_name: '', scheme_flag: 0,
      scheme_master_list1: [], selectedSchemeFile: '', scheme_file_name: '',
      scheme_mapping_stored_file_name: '', merchant_master_list: [],
      scheme_activer: [], scheme_activer_name: [], merchant_activer: [],
      party_master_id: 3, emiMenus: [], party_name: null, admin_counter: null,
      merchantList: [], chainList: [], merchantID: '', chainID: '', storeID: '',
      merchantValue: [], skuMapValue: [], chainValue: [], storeList: [], storeValue: [],
      party_list: [],
      party_options: [{ value: 'Brand', label: 'Brand' }, { value: 'Merchant', label: 'Merchant' }],
      selected_party_type: null, selected_party: null,
      selected_party_type2: null, selected_party2: [{ value: null, label: null }],
      selected_emi_type_value: null, selceted_emi_type_value_label: null,
      selected_emi_type: [
        { value: 'ALL', label: 'ALL' }, { value: 'Unipaynext', label: 'Unipaynext' },
        { value: 'TotalEMI', label: 'TotalEMI' }, { value: 'TrueEMI', label: 'TrueEMI' },
      ],
      select_product_button: false, create_button_disabler: false,
      skuMappingList: { 0: [] }, schemeMappingList: { 0: [] }, adderArray: [1],
      all_mapper: true, freeemi_wallet: false,
      scheme_super_list: [], scheme_super_list_all: [], model_list_all: [],
      enableDisablePartyType: false,
      segmentOptions: [
        { value: 'GT', label: 'GT (General Trade)' }, { value: 'ROT', label: 'ROT (Regional Outlet Trade)' },
        { value: 'EBO', label: 'EBO (Exclusive Brand Outlet)' }, { value: 'MBO', label: 'MBO (Multi-brand Outlet)' },
        { value: 'LFR', label: 'LFR (Large Format Retail)' }, { value: 'MT', label: 'MT' },
      ],
      selectedSegment: [],
      stateOptions: [
        { value: 'AP', label: 'Andhra Pradesh' }, { value: 'AR', label: 'Arunachal Pradesh' },
        { value: 'AS', label: 'Assam' }, { value: 'BR', label: 'Bihar' },
        { value: 'CG', label: 'Chhattisgarh' }, { value: 'GA', label: 'Goa' },
        { value: 'GJ', label: 'Gujarat' }, { value: 'HR', label: 'Haryana' },
        { value: 'HP', label: 'Himachal Pradesh' }, { value: 'JK', label: 'Jammu and Kashmir' },
        { value: 'JH', label: 'Jharkhand' }, { value: 'KA', label: 'Karnataka' },
        { value: 'KL', label: 'Kerala' }, { value: 'MP', label: 'Madhya Pradesh' },
        { value: 'MH', label: 'Maharashtra' }, { value: 'MN', label: 'Manipur' },
        { value: 'ML', label: 'Meghalaya' }, { value: 'MZ', label: 'Mizoram' },
        { value: 'NL', label: 'Nagaland' }, { value: 'OR', label: 'Orissa' },
        { value: 'PB', label: 'Punjab' }, { value: 'RJ', label: 'Rajasthan' },
        { value: 'SK', label: 'Sikkim' }, { value: 'TN', label: 'Tamil Nadu' },
        { value: 'TR', label: 'Tripura' }, { value: 'UP', label: 'Uttar Pradesh' },
        { value: 'UK', label: 'Uttarakhand' }, { value: 'WB', label: 'West Bengal' },
        { value: 'AN', label: 'Andaman and Nicobar Islands' }, { value: 'CH', label: 'Chandigarh' },
        { value: 'DH', label: 'Dadra and Nagar Haveli' }, { value: 'DD', label: 'Daman and Diu' },
        { value: 'DL', label: 'Delhi' }, { value: 'TEL', label: 'Telangana' },
        { value: 'LD', label: 'Lakshadweep' }, { value: 'PY', label: 'Pondicherry' },
      ],
      cityOptions: [],
      segmentSuffixType: 'EXCLUDE', stateSuffixType: 'EXCLUDE', citySuffixType: 'EXCLUDE',
      suffixPreview: 'Promotion name will be auto-generated',
      canCreatePromotion: false,

      // ── React-side search + pagination ────────────────────
      // "Raw" = bound to the <input> immediately (responsive feel)
      // non-Raw = set by 250ms debounce (filter only after pause)
      skuSearchRaw: '', skuSearch: '', skuPage: 1, skuPageSize: 10,
      schSearchRaw: '', schSearch: '', schPage: 1, schPageSize: 10,
    };

    this.myRef = React.createRef();
    this.myRef2 = React.createRef();

    // Debounced promotion-name recompute
    this.debouncedUpdatePromotionName = debounce(this._doUpdatePromotionName, 150);

    // Debounced search — filter runs 250ms after typing stops
    this._applySkuSearch = debounce(v => this.setState({ skuSearch: v, skuPage: 1 }), 250);
    this._applySchSearch = debounce(v => this.setState({ schSearch: v, schPage: 1 }), 250);

    // Stable function refs so memo rows never see a new prop
    this._boundChangeSKU = this.changeSKU;
    this._boundChangeScheme = this.changeScheme;
  }

  // ── Promotion name helpers ────────────────────────────────

  formatMonth = m => ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][m];

  formatDateRange = (s, e) => {
    const sd = new Date(s), ed = new Date(e);
    return `${sd.getDate()}_${this.formatMonth(sd.getMonth())}_${ed.getDate()}`;
  };

  getBrandName = () => {
    let n = 'BRAND';
    if (this.state.selected_party2?.[0]?.label) n = this.state.selected_party2[0].label;
    else if (this.state.party_name) n = this.state.party_name;
    return n.split(' ').filter(w => w.toLowerCase() !== 'mobiles').join(' ').toUpperCase();
  };

  getModelString = () => {
    const m = this.state.model_list;
    if (!m || !Object.keys(m).length) return 'NO_MODEL';
    const ids = Object.keys(m).filter(id => m[id]);
    if (!ids.length) return 'NO_MODEL';
    if (ids.length < 3) {
      return ids.map(id => {
        const d = this.state.raw_display_data.find(r => r.modelDisplayNumber == id);
        return d ? d.modelDisplayNumber.toUpperCase() : '';
      }).join('_');
    }
    return 'ALL';
  };

  getBankString = () => {
    const { scheme_master_list, scheme_master_data } = this.state;
    const sel = Object.keys(scheme_master_list || {}).filter(id => scheme_master_list[id]).sort().join(',');
    const key = `bank_${sel}`;
    if (this._promotionNameCache[key]) return this._promotionNameCache[key];
    if (!sel) return 'NO_BANK';

    const shortCode = n => {
      const u = n.toUpperCase();
      if (u.includes('AU BANK')) return 'AU';
      if (u.includes('AXIS')) return 'AXIS';
      if (u.includes('HDFC')) return 'HDFC';
      if (u.includes('AMERICAN EXPRESS') || u.includes('AMEX')) return 'AMEX';
      if (u.includes('CITI')) return 'CITI';
      if (u.includes('ICICI')) return 'ICICI';
      if (u.includes('INDUSIND')) return 'INDUSIND';
      if (u.includes('KOTAK')) return 'KOTAK';
      if (u.includes('RBL')) return 'RBL';
      if (u.includes('SBI')) return 'SBI';
      if (u.includes('STANDARD CHARTERED') || u.includes('SCB')) return 'SCB';
      if (u.includes('YES')) return 'YES';
      if (u.includes('HSBC')) return 'HSBC';
      if (u.includes('BANK OF BARODA') || u.includes('BOB')) return 'BOB';
      return n.split(' ')[0].toUpperCase();
    };
    const codes = new Set();
    sel.split(',').forEach(id => {
      const s = scheme_master_data.find(x => x.id == id);
      if (s?.bankName) codes.add(shortCode(s.bankName));
    });
    const arr = Array.from(codes);
    const res = arr.length === 0 ? 'NO_BANK' : arr.length === 1 ? arr[0] : arr.length === 2 ? arr.join('_') : 'ALL';
    this._promotionNameCache[key] = res;
    return res;
  };

  canCreatePromotion = () => {
    const decoded = jwt_decode(localStorage.usertoken);
    return process.env.REACT_APP_ALLOWED_USER.includes(decoded._id);
  };

  getEmiTypeString = () => {
    const { scheme_master_list, scheme_master_data } = this.state;
    const ids = Object.keys(scheme_master_list || {}).filter(id => scheme_master_list[id]);
    if (!ids.length) return '';
    const t = new Set();
    ids.forEach(id => {
      const s = scheme_master_data.find(x => x.id == id);
      if (!s) return;
      if (s.emiType === 'Low Cost') t.add(parseFloat(s.lowCostSubvention) === 0 ? 'CIB' : 'LCE');
      else if (s.emiType === 'Zero Cost') t.add('NCE');
      else if (s.emiType === 'Flat') t.add('FLAT');
    });
    const a = Array.from(t);
    return a.length === 0 ? '' : a.length === 1 ? a[0] : a.join('_');
  };

  getCashbackValue = () => {
    const { scheme_master_list, scheme_master_data } = this.state;
    const ids = Object.keys(scheme_master_list).filter(id => scheme_master_list[id] === true);
    if (!ids.length) return '';
    const toK = v => {
      if (!v || v === '' || v === 'NA') return '';
      const n = Number(v);
      if (isNaN(n) || n === 0) return '';
      if (n >= 1000) return (Math.floor((n / 1000) * 100) / 100).toString().replace(/\.0+$/, '') + 'K';
      return n.toString();
    };
    const vals = new Set();
    ids.forEach(id => {
      const s = scheme_master_data.find(x => x.id == id);
      if (!s) return;
      const ua = parseFloat(s.upfrontBrandSubAmount) || 0, um = parseFloat(s.upfrontMaxCashback) || 0;
      const pa = parseFloat(s.postBrandSubAmount) || 0, pm = parseFloat(s.postBrandMaxAmount) || 0;
      let v = '';
      if (um > 0) {
        if (ua > 0) v = `${ua}P_${toK(um)}`;
        else if ((s.upfrontBrandSub || '').toLowerCase() === 'fl') v = toK(um);
      } else if (pa > 0) {
        if (pm > 0) v = `${pa}P_${toK(pm)}`;
        else if ((s.postBrandSub || '').toLowerCase() === 'cashback') v = toK(pa);
      }
      if (v) vals.add(v);
    });
    const r = Array.from(vals);
    return r.length === 0 ? '' : r.length === 1 ? r[0] : r.slice(0, 2).join('_');
  };

  getCashbackType = () => {
    const { scheme_master_list, scheme_master_data } = this.state;
    const brand = this.getBrandName();
    const ids = Object.keys(scheme_master_list).filter(id => scheme_master_list[id] === true);
    if (!ids.length) return '';
    const t = new Set();
    ids.forEach(id => {
      const s = scheme_master_data.find(x => x.id == id);
      if (!s) return;
      const ua = parseFloat(s.upfrontBrandSubAmount);
      const isNA = s.postBrandSubAmount === 'NA' || s.postBrandSubAmount === '';
      const pa = isNA ? 0 : parseFloat(s.postBrandSubAmount);
      if (ua === 0 && isNA) return;
      if (!isNA && pa > 0) t.add('CB');
      else if (ua > 0 && isNA) t.add(brand.includes('SAMSUNG') ? 'IBD' : 'ICB');
    });
    const a = Array.from(t);
    return a.length === 0 ? '' : a.length === 1 ? a[0] : a.slice(0, 2).join('_');
  };

  getCoFunding = () => {
    const { scheme_master_list, scheme_master_data } = this.state;
    const ids = Object.keys(scheme_master_list).filter(id => scheme_master_list[id] == true);
    if (!ids.length) return false;
    const vals = scheme_master_data.filter(x => ids.includes(x.id));
    if (!vals.length) return false;
    // Check if any selected scheme has SBI or HDFC as the bank
    const hasSBIOrHDFC = vals.some(x => {
      const bankName = (x.bankName || '').toUpperCase();
      return bankName.includes('SBI') || bankName.includes('HDFC');
    });
    // If no SBI or HDFC, return false immediately
    if (!hasSBIOrHDFC) return false;
    const varCB = vals.some(x => Number(x.bankMaxCb) > 0 && Number(x.bankCbPer) > 0);
    const coFunded = vals.some(x => {
      const ub = Number(x.upfrontCashbackBrand) || 0, ubk = Number(x.upfrontCashbackBank) || 0;
      const pb = Number(x.postCashbackBrand) || 0, pbk = Number(x.postCashbackBank) || 0;
      return (ub !== 100 && ub + ubk === 100 && pb === 100)
        || (ub === 100 && pb !== 100 && pb + pbk === 100);
    });
    return coFunded || varCB;
  };

  canGeneratePromotionName = () => {
    if (!this.state.party_master_id) return false;
    const hasSchemes = Object.keys(this.state.scheme_master_list || {}).some(id => this.state.scheme_master_list[id] === true);
    if (!hasSchemes) return false;
    return Object.keys(this.state.model_list || {}).some(id => this.state.model_list[id] === true);
  };

  generatePromotionName = () => {
    if (!this.canGeneratePromotionName()) return undefined;
    const brand = this.getBrandName();
    const dateRange = this.formatDateRange(this.state.startDate, this.state.endDate);
    const emi = this.getEmiTypeString();
    const model = this.getModelString();
    const bank = this.getBankString();
    const cbType = this.getCashbackType();
    const cbVal = this.getCashbackValue();
    const coFund = this.getCoFunding() ? 'Cofunding' : '';
    let base = `${brand}_${dateRange}_${emi}_${model}_${bank}_${cbType}_${cbVal}${coFund ? '_' + coFund : ''}`;
    base = base.replace(/_+/g, '_').replace(/_$/, '');
    return base + this.generateSuffixOnly();
  };

  generateSuffixOnly = () => {
    const { selectedSegment, selectedState, selectedCity, segmentSuffixType, stateSuffixType, citySuffixType } = this.state;
    const p = [];
    if (selectedSegment?.length) { const v = selectedSegment.map(s => s.value); p.push(segmentSuffixType === 'EXCLUDE' ? `_EX_SEG_${v.join('_')}` : `_IN_SEG_${v.join('_')}`); }
    if (selectedState?.length) { const v = selectedState.map(s => s.value); p.push(stateSuffixType === 'EXCLUDE' ? `_EX_STATE_${v.join('_')}` : `_IN_STATE_${v.join('_')}`); }
    if (selectedCity?.length) { selectedCity.forEach(c => p.push(citySuffixType === 'EXCLUDE' ? `_EX_CITY_${c.value}` : `_ONLY_CITY_${c.value}`)); }
    return p.join('');
  };

  generateSuffix = () => {
    const sfx = this.generateSuffixOnly();
    const name = this.generatePromotionName();
    this.setState({ campaign_name: name, suffixPreview: sfx });
  };

  _doUpdatePromotionName = () => {
    const name = this.generatePromotionName();
    if (name !== undefined) this.setState({ campaign_name: name });
  };

  updatePromotionName = () => this.debouncedUpdatePromotionName();

  // ── Suffix / filter handlers ──────────────────────────────

  handleSegmentSuffixTypeChange = o => this.setState({ segmentSuffixType: o?.value || 'EXCLUDE' }, this.generateSuffix);
  handleStateSuffixTypeChange = o => this.setState({ stateSuffixType: o?.value || 'EXCLUDE' }, this.generateSuffix);
  handleCitySuffixTypeChange = o => this.setState({ citySuffixType: o?.value || 'EXCLUDE' }, this.generateSuffix);
  handleSegmentChange = v => this.setState({ selectedSegment: v }, this.generateSuffix);
  handleStateChange = v => this.setState({ selectedState: v || [], selectedCity: [] }, this.generateSuffix);
  handleCityChange = v => this.setState({ selectedCity: v }, this.generateSuffix);

  // ── Search handlers ───────────────────────────────────────
  // Input updates immediately (skuSearchRaw → controlled input)
  // Filter applies after 250ms pause (skuSearch → filterSku)

  handleSkuSearch = v => { this.setState({ skuSearchRaw: v }); this._applySkuSearch(v); };
  handleSkuPageSize = n => this.setState({ skuPageSize: n, skuPage: 1 });
  handleSkuPage = p => this.setState({ skuPage: p });

  handleSchSearch = v => { this.setState({ schSearchRaw: v }); this._applySchSearch(v); };
  handleSchPageSize = n => this.setState({ schPageSize: n, schPage: 1 });
  handleSchPage = p => this.setState({ schPage: p });

  // ── Notifications ─────────────────────────────────────────

  handleNotificationClick = (pos, color, msg) => {
    const lvl = { 1: 'success', 2: 'warning', 3: 'error', 4: 'info' };
    this.state._notificationSystem.addNotification({
      title: <span data-notify="icon" className="pe-7s-gift" />,
      message: <div>{msg}</div>, level: lvl[color] || 'info', position: pos, autoDismiss: 5,
    });
  };

  // ── Party ─────────────────────────────────────────────────

  getPartyList = () => {
    this.setState({ party_list: [], selected_party: null });
    axios.get(config.get('API_URL') + '/brandemi/findAllManufacturers', { headers: { Authorization: ` ${localStorage.usertoken}` } })
      .then(r => this.setState({ party_list: r.data.map(v => ({ value: v.id, label: v.party_name })), loading: false }));
  };

  updateFreeEmiWallet = e => this.setState({ freeemi_wallet: e.target.checked });

  updatePartyType = v => {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have access to create promotions."); return; }
    if (v.value === 'Merchant') { this.handleNotificationClick("tr", 3, "Merchant promotions is under construction."); }
    else {
      this.setState({
        selected_party_type: v.value, selected_party_type2: [{ value: v.value, label: v.label }],
        loading: true, selected_party: null, selected_party2: [{ value: null, label: null }],
        party_master_id: null, party_list: [],
      }, this.getPartyList);
    }
  };

  updatePartyValue = v => {
    this._promotionNameCache = {};
    this.setState({
      selected_party: v.value, selected_party2: [{ value: v.value, label: v.label }],
      party_master_id: v.value, loading: true,
      sku_list: [], sku_list_name: [], model_list_name: [], scheme_master_list: [], scheme_list_name: [],
      stepper: [false, true, true, true, true, true], current_tab: 'vertical-tab-zero', select_product_button: true,
      skuSearch: '', skuSearchRaw: '', skuPage: 1,
      schSearch: '', schSearchRaw: '', schPage: 1,
    }, () => {
      Promise.all([this._refreshSku_Show_View(), this.getSchemeMasterDataView()])
        .then(() => this.debouncedUpdatePromotionName());
    });
  };

  updateEmiTypeValue = v => this.setState({ selected_emi_type_value: v.value, selceted_emi_type_value_label: [{ value: v.value, label: v.label }] });

  // ── Data fetch ────────────────────────────────────────────
  // NOTE: No DataTable calls here. componentDidUpdate is GONE.
  // Switching tabs is now instant — no jQuery blocking the thread.

  _refreshSku_Show_View() {
    return axios.get(config.get('API_URL') + '/brandemi/view_brand_sku1', {
      headers: { Authorization: ` ${localStorage.usertoken}` },
      params: { party_master_id: this.state.party_master_id },
    }).then(r => {
      if (r.status === 200 && Array.isArray(r.data)) {
        const o1 = {}, o2 = {};
        r.data.forEach(x => { o1[x.brandSku] = true; o2[x.modelDisplayNumber] = true; });
        this.setState({ raw_display_data: r.data, sku_list1: o1, model_list_all: o2, loading: false, select_product_button: false });
      } else { this.setState({ loading: false }); }
    }).catch(() => { this.handleNotificationClick("tr", 3, "Error fetching brand SKU data"); this.setState({ loading: false }); });
  }

  getSchemeMasterDataView() {
    return axios.get(config.get('API_URL') + '/brandemi/get_new_schemes_master1', {
      headers: { Authorization: ` ${localStorage.usertoken}` },
      params: { party_master_id: this.state.party_master_id },
    }).then(r => {
      if (r.status === 200 && Array.isArray(r.data)) {
        const r3 = {}, r4 = {}, r5 = {};
        r.data.forEach(x => { r3[x.id] = true; r4[x.name] = true; r5[x.id] = x.scheme_name; });
        this._promotionNameCache = {};
        this.setState({ scheme_master_data: r.data, scheme_activer: r3, scheme_activer_name: r4, scheme_super_list_all: r5 });
      } else { this.setState({ loading: false }); }
    }).catch(() => { this.handleNotificationClick("tr", 3, "Error fetching scheme data"); this.setState({ loading: false }); });
  }

  // ── Lifecycle ─────────────────────────────────────────────

  componentDidMount() {
    this.setState({ canCreatePromotion: this.canCreatePromotion(), _notificationSystem: this.refs.notificationSystem });
    this.validator = new SimpleReactValidator();
    Promise.all([this._refreshSku_Show_View(), this.getSchemeMasterDataView(), this._getAllMerchants()])
      .then(() => this.debouncedUpdatePromotionName());
    const loc = JSON.parse(localStorage.getItem("locPromoFlexipay"));
    if (loc === '1' || loc === 1) {
      this.setState({
        selected_party_type2: [{ value: "Brand", label: "Brand" }], selected_party2: [{ value: 35, label: "Default" }],
        selected_party_type: [{ value: "Brand", label: "Brand" }], selected_party: [{ value: 35, label: "Default" }],
        enableDisablePartyType: true,
      });
    }
  }

  componentWillUnmount() {
    this.debouncedUpdatePromotionName.cancel();
    this._applySkuSearch.cancel();
    this._applySchSearch.cancel();
  }

  // ── Toggle handlers ───────────────────────────────────────

  changeSKU = (sku, model, checked) => {
    const newML = { ...this.state.model_list };
    if (!checked) delete newML[model]; else newML[model] = true;
    this.setState({
      sku_list: { ...this.state.sku_list, [sku]: checked }, model_list: newML,
      mapField2: false, mapField: false, current_tab: 'vertical-tab-one',
      stepper: [false, false, true, true, true, true], scheme_list_name: [], model_list_name: [],
    }, this.debouncedUpdatePromotionName);
  };

  changeScheme = (id, name, checked) => {
    const newSL = { ...this.state.scheme_super_list };
    if (!checked) delete newSL[id]; else newSL[id] = name;
    this._promotionNameCache = {};
    this.setState({
      scheme_master_list: { ...this.state.scheme_master_list, [id]: checked }, scheme_super_list: newSL,
      current_tab: 'vertical-tab-two', stepper: [false, false, false, true, true, true],
      scheme_list_name: [], model_list_name: [],
    }, this.debouncedUpdatePromotionName);
  };

  changeMerchant = (id, checked) => this.setState({ merchant_master_list: { ...this.state.merchant_master_list, [id]: checked } });

  // ── Bank / tenure ─────────────────────────────────────────

  changeTenure(i, m, e) {
    const bo = this.state.bankOptions.map((b, bi) => bi === i ? { ...b, [m]: e.target.checked } : b);
    this.setState({ bankOptions: bo, mapField2: false });
  }
  changeActiver(i, e) { this.setState({ activer: { ...this.state.activer, ['' + i + '']: e.target.checked }, mapField: false }); }
  changeEMIstatus(i, e) { this.setState({ isEMI: { ...this.state.isEMI, [i]: e.target.checked }, mapField2: false }); }
  changeZeroCoststatus(i, e) { this.setState({ zeroCost: { ...this.state.zeroCost, [i]: e.target.checked }, mapField2: false }); }
  changeisUpfrontstatus(i, e) { this.setState({ isUpfront: { ...this.state.isUpfront, [i]: e.target.checked }, mapField2: false }); }

  updateBrandSubvention = (i, o) => this.setState({ brand_subvention_list: { ...this.state.brand_subvention_list, [i]: o.value }, mapField2: false });
  updateBankSubvention = (i, o) => this.setState({
    disabler_list: { ...this.state.disabler_list, [i]: o.value === 'NA' },
    bank_subvention_list: { ...this.state.bank_subvention_list, [i]: o.value }, mapField2: false,
  });

  // ── Dates ─────────────────────────────────────────────────

  handleFromChange = d => { this._promotionNameCache = {}; this.setState({ startDate: moment(d).format('YYYY-MM-DD'), mapField2: false }, this.debouncedUpdatePromotionName); };
  handleToChange = d => { this._promotionNameCache = {}; this.setState({ endDate: moment(d).format('YYYY-MM-DD'), mapField2: false }, this.debouncedUpdatePromotionName); };

  // ── Select all / deselect ─────────────────────────────────

  selectAll() { this.setState({ sku_list: this.state.sku_list1, model_list: this.state.model_list_all, scheme_list_name: [], sku_list_name: [], model_list_name: [], current_tab: 'vertical-tab-one', stepper: [false, false, true, true, true, true] }, this.debouncedUpdatePromotionName); }
  deSelectAll() { this.setState({ sku_list: [], scheme_list_name: [], model_list: [], model_list_name: [], sku_list_name: [], current_tab: 'vertical-tab-one', stepper: [false, false, true, true, true, true] }, this.debouncedUpdatePromotionName); }
  selectAllPromotions() { this._promotionNameCache = {}; this.setState({ scheme_master_list: this.state.scheme_activer, scheme_super_list: this.state.scheme_super_list_all, current_tab: 'vertical-tab-two', stepper: [false, false, false, true, true, true], scheme_list_name: [], model_list_name: [] }, this.debouncedUpdatePromotionName); }
  deselectAllPromotions() { this._promotionNameCache = {}; this.setState({ scheme_master_list: [], scheme_super_list: [], current_tab: 'vertical-tab-two', stepper: [false, false, false, true, true, true], scheme_list_name: [], model_list_name: [] }, this.debouncedUpdatePromotionName); }
  selectAllBanks() { this.setState({ activer: this.state.activer2, mapField: false }); }
  deSelectAllBanks() { this.setState({ activer: { '0': false, '1': false, '2': false, '3': false, '4': false, '5': false, '6': false, '7': false, '8': false, '9': false, '10': false, '11': false }, mapField: false }); }

  // ── Navigation ────────────────────────────────────────────

  tabClicker(v) {
    const m = {
      0: { stepper: [false, true, true, true, true, true], current_tab: 'vertical-tab-zero' },
      1: { current_tab: 'vertical-tab-one' }, 2: { current_tab: 'vertical-tab-two' },
      3: { current_tab: 'vertical-tab-three' }, 4: { current_tab: 'vertical-tab-four' }, 5: { current_tab: 'vertical-tab-five' },
    };
    const t = m[v] || {};
    this._visitedTabs.add(t.current_tab);
    this.setState(t);
  }

  openProducts = status => {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    if (!this.state.selected_party || !this.state.selected_party_type) { this.handleNotificationClick("tr", 3, "Please select a party"); }
    else { this._visitedTabs.add('vertical-tab-one'); this.setState({ mapField2: status, current_tab: 'vertical-tab-one', stepper: { ...this.state.stepper, [1]: false } }); }
  };

  openSectionToBanks = status => {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    // KEY FIX: derive counter here — no DataTable init triggered
    const sku_monitor = Object.values(this.state.sku_list).filter(Boolean).length;
    if (sku_monitor === 0) { this.handleNotificationClick("tr", 3, "Select atleast one SKU"); }
    else {
      this._visitedTabs.add('vertical-tab-two');
      // Single setState — no componentDidUpdate side effects
      this.setState({ mapField2: status, current_tab: 'vertical-tab-two', stepper: { ...this.state.stepper, [2]: false } });
    }
  };

  sendEMIMappings(status) {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    const scheme_monitor = Object.values(this.state.scheme_master_list || {}).filter(Boolean).length;
    if (scheme_monitor === 0) { this.handleNotificationClick("tr", 3, "Select atleast one scheme"); }
    else {
      this._visitedTabs.add('vertical-tab-three');
      this.setState({ mapField2: status, current_tab: 'vertical-tab-three', stepper: { ...this.state.stepper, [3]: false } }, () => {
        this.skuOptionsCreator(); this.schemesOptionsCreator(); this.debouncedUpdatePromotionName();
      });
    }
  }

  openSectionToProduct = status => {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    const skuM = Object.values(this.state.sku_list).filter(Boolean).length;
    const schM = Object.values(this.state.scheme_master_list || {}).filter(Boolean).length;
    if (skuM === 0) { this.handleNotificationClick("tr", 3, "Select atleast one SKU"); }
    else if (schM === 0) { this.handleNotificationClick("tr", 3, "Select atleast one scheme"); }
    else if (!this.state.merchantValue.length) { this.handleNotificationClick("tr", 3, "Select atleast one merchant"); }
    else if (!this.state.campaign_name) { this.handleNotificationClick("tr", 3, "Please enter promotion name"); }
    else if (!this.state.campaign_desc) { this.handleNotificationClick("tr", 3, "Please enter promotion description"); }
    else if (this.validateDate() !== 0) { this.handleNotificationClick("tr", 3, "Please insert valid date range"); }
    else if ((this.state.maxTxnAmount * 1) <= 0 || this.state.maxTxnAmount === '') { this.handleNotificationClick("tr", 3, "Please insert value greater than 0"); }
    else if (!this.state.selected_emi_type_value) { this.handleNotificationClick("tr", 3, "Please select emi type"); }
    else { this.setState({ mapField0: status, newConfirmationModel: !this.state.newConfirmationModel }); }
  };

  merchantSubmitHandler() {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    if (!this.state.merchantValue.length) { this.handleNotificationClick("tr", 3, "Select atleast one merchant"); }
    else { this._visitedTabs.add('vertical-tab-five'); this.setState({ isSubmit: true, current_tab: 'vertical-tab-five', stepper: { ...this.state.stepper, [5]: false } }); }
  }

  mapSchemesToProducts() {
    if (!this.state.canCreatePromotion) { this.handleNotificationClick("tr", 3, "You don't have permission"); return; }
    this.setState({ loading: true });
    let kill = 0;
    const len = Object.keys(this.state.skuMappingList).length;
    for (let i = 1; i < len; i++) {
      if (this.state.skuMappingList[i].length === 0 && this.state.schemeMappingList[i].length === 0) {
        this.handleNotificationClick("tr", 3, `Both schemes and sku can't be ALL at row ${i + 1}`); kill = 1;
      }
    }
    if (kill === 0) { this._visitedTabs.add('vertical-tab-four'); this.setState({ isSubmit: true, current_tab: 'vertical-tab-four', stepper: { ...this.state.stepper, [4]: false }, loading: false }); }
    else { this.setState({ loading: false }); }
  }

  validateDate() { return this.state.startDate > this.state.endDate ? 1 : 0; }

  toggleConfirmationModel() {
    const m = Object.values(this.state.sku_list).filter(Boolean).length;
    if (m === 0) { this.handleNotificationClick("tr", 3, "Select atleast one SKU"); }
    else { this.setState({ newConfirmationModel: !this.state.newConfirmationModel }); }
  }

  // ── Merchants ─────────────────────────────────────────────

  _getAllMerchants() {
    return axios.post(config.get('API_URL') + '/brandemi/get-merchant', {}, { headers: { Authorization: ` ${localStorage.usertoken}` } })
      .then(r => { if (Array.isArray(r.data) && r.data.length) r.data.forEach(m => this.state.merchantList.push({ value: m.merchant_code, label: m.merchant_name })); });
  }

  changeMerchant1(o) {
    const ids = o.length < 1 ? [] : o.map(m => m.value);
    this.setState({ merchantID: ids, chainID: '', merchantValue: o, chainValue: [], storeID: '', storeValue: [], current_tab: 'vertical-tab-four', stepper: [false, false, false, false, false, true] },
      () => { this.getChain(ids, 'merchant'); this.getStore(ids, 'merchant'); });
  }

  getChain(code, type) {
    axios.post(config.get('API_URL') + '/brandemi/get-chain', { code, type }, { headers: { Authorization: ` ${localStorage.usertoken}` } })
      .then(r => this.setState({ chainList: r.data.map(c => ({ value: c.chain_code, label: c.chain_name, value1: c.merchant_code, merch_name: c.merchant_name })), chainID: '' }));
  }

  changeChain(o) {
    const ids = o.length < 1 ? [] : o.map(c => c.value);
    this.setState({ chainID: ids, chainValue: o, storeID: '', storeValue: [], current_tab: 'vertical-tab-four', stepper: [false, false, false, false, false, true] },
      () => this.getStore(ids, 'chain', null, this.state.merchantID));
  }

  getStore(code, type, ot, oc) {
    axios.post(config.get('API_URL') + '/brandemi/get-store', { code, type, object_type: ot, object_code: oc }, { headers: { Authorization: ` ${localStorage.usertoken}` } })
      .then(r => this.setState({ storeList: r.data.map(s => ({ value: s.store_code, label: s.store_name, value1: s.merchant_code, value2: s.chain_code, chain_name: s.chain_name, merch_name: s.merchant_name })), storeID: '' }));
  }

  changeStore(o) {
    const ids = o.length < 1 ? [] : o.map(s => s.value);
    this.setState({ storeID: ids, storeValue: o, current_tab: 'vertical-tab-four', stepper: [false, false, false, false, false, true] });
  }

  // ── Scheme/SKU mapping ────────────────────────────────────

  skuOptionsCreator() { Object.entries(this.state.model_list).forEach(([k, v]) => { if (v) this.state.model_list_name.push({ value: k, label: k }); }); }
  schemesOptionsCreator() { Object.entries(this.state.scheme_super_list).forEach(([k, v]) => this.state.scheme_list_name.push({ value: k, label: v })); }

  changeSkuMap(i, o) { this.setState({ skuMappingList: { ...this.state.skuMappingList, [i]: o }, current_tab: 'vertical-tab-three', stepper: [false, false, false, false, true, true] }); }
  changeSchemeMap(i, o) { this.setState({ schemeMappingList: { ...this.state.schemeMappingList, [i]: o }, current_tab: 'vertical-tab-three', stepper: [false, false, false, false, true, true] }); }

  addArray() {
    const a = [...this.state.adderArray, 1];
    this.setState({ schemeMappingList: { ...this.state.schemeMappingList, [a.length - 1]: [] }, skuMappingList: { ...this.state.skuMappingList, [a.length - 1]: [] }, adderArray: a, current_tab: 'vertical-tab-three', stepper: [false, false, false, false, true, true] });
  }

  popArray() {
    const a = [...this.state.adderArray]; a.pop();
    const sm = { ...this.state.schemeMappingList }, sk = { ...this.state.skuMappingList };
    delete sm[a.length]; delete sk[a.length];
    this.setState({ schemeMappingList: sm, skuMappingList: sk, adderArray: a, current_tab: 'vertical-tab-three', stepper: [false, false, false, false, true, true] });
  }

  // ── Submit ────────────────────────────────────────────────

  handleDuplicatePromotionName() {
    const cur = this.state.campaign_name, m = cur.match(/_(\d+)$/);
    const base = m ? cur.replace(/_(\d+)$/, '') : cur, n = m ? parseInt(m[1]) + 1 : 2, next = `${base}_${n}`;
    this.setState({ campaign_name: next, newConfirmationModel: true, create_button_disabler: false },
      () => this.handleNotificationClick("tr", 2, `Promotion "${cur}" already exists. Renamed to "${next}". Click Create again.`));
  }

  sendCompleteScheme() {
    this.setState({ create_button_disabler: true });
    const fd = new FormData(), dec = jwt_decode(localStorage.usertoken);
    fd.append('sku_list', JSON.stringify(this.state.sku_list));
    fd.append('user_id', dec._id);
    fd.append('start_date', this.state.startDate);
    fd.append('end_date', this.state.endDate);
    fd.append('max_cashback', this.state.maxTxnAmount);
    fd.append('camp_name', this.state.campaign_name);
    fd.append('camp_desc', this.state.campaign_desc);
    fd.append('scheme_master_list', JSON.stringify(this.state.scheme_master_list));
    fd.append('party_master_id', this.state.party_master_id);
    fd.append('merchant_list', JSON.stringify(this.state.merchantValue));
    fd.append('chain_list', JSON.stringify(this.state.chainValue));
    fd.append('store_list', JSON.stringify(this.state.storeValue));
    fd.append('scheme_sku_map', JSON.stringify(this.state.skuMappingList));
    fd.append('scheme_sku_map2', JSON.stringify(this.state.schemeMappingList));
    fd.append('freeemi_wallet', this.state.freeemi_wallet);
    fd.append('emi_type', this.state.selected_emi_type_value);
    if (this.state.selectedSegment) fd.append('segment', this.state.selectedSegment.value);
    if (this.state.selectedState) { fd.append('state', this.state.selectedState.value); fd.append('state_name', this.state.selectedState.label); }
    if (this.state.selectedCity) { fd.append('city', this.state.selectedCity.value); fd.append('city_name', this.state.selectedCity.label); }

    axios.post(config.get('API_URL') + '/brandemi/new_submit_subvention_scheme_new', fd, { headers: { Authorization: ` ${localStorage.usertoken}` } })
      .then(r => {
        if (r.data.flag === '-1') { this.setState({ create_button_disabler: false }); this.handleDuplicatePromotionName(); }
        else if (r.data.flag === '2') { this.setState({ newConfirmationModel: false, create_button_disabler: false }); this.handleNotificationClick("tr", 3, "Unable to create the promotion"); }
        else { this.handleNotificationClick("tr", 1, "Data successfully uploaded"); this.props.history.push('/admin/admin-promotion-master'); }
      }).catch(() => this.setState({ create_button_disabler: false, newConfirmationModel: false }));
  }

  // ── Misc ──────────────────────────────────────────────────

  whetherDisabled(i) { return this.state.brand_subvention_list[i] !== "Percent"; }
  openSectionToMap = s => this.setState({ mapField: s });
  updateMapVisiblity = v => this.setState({ mapField: v });
  goBack = () => this.props.history.push('/admin/admin-promotion-master');

  evalMerchantCounter() { this.setState(s => ({ merchant_counter: s.merchant_counter + 1, merchant_counter_array: [...s.merchant_counter_array, 0] })); }
  negEvalMerchantCounter() { this.setState(s => { const a = [...s.merchant_counter_array]; a.pop(); return { merchant_counter: s.merchant_counter + 1, merchant_counter_array: a }; }); }

  updateMerchantSubvention = o => {
    const r = [false, false, false, false, false];
    const m = { State_Level: [true], City_Level: [true, true], Merchant_Level: [true, true, true], Chain_Level: [true, true, true, true], Store_Level: [true, true, true, true, true] };
    (m[o.value] || []).forEach((v, i) => { r[i] = v; });
    this.setState({ selectedMerchantSubvention: o.value, refinerMerch: r, mapField2: false, selectedMerchValues: [], slicedMerchValues: [], refineSearch: false });
  };

  updateMerchValues = (o, v) => {
    if (o === null) this.setState({ selectedMerchValues: [], mapField2: false });
    else this.setState({ selectedMerchValues: o, merchant_ida: v, mapField2: false });
  };

  _getMerchList = async (merch_config, search, loaded, { page }) => {
    const cm = { State_Level: { a: 'state_id', b: 'state_name', c: 'state_id' }, City_Level: { a: 'city_id', b: 'city_name', c: 'city_id' }, Merchant_Level: { a: 'merchant_id', b: 'merchant_name', c: 'merchant_code' }, Chain_Level: { a: 'chain_id', b: 'chain_name', c: 'chain_code' }, Store_Level: { a: 'store_id', b: 'store_name', c: 'store_code' } };
    const { a, b, c } = cm[merch_config] || {};
    const r = await axios.get(`${config.get('API_URL')}/brandemi/get_merch_values/?merch_config=${merch_config}&search=${search}&page=${page}`, { headers: { Authorization: ` ${localStorage.usertoken}` } });
    return { options: r.data.map(x => ({ value: x[a], label: x[b], value1: x[c] })), hasMore: Array.isArray(r.data) && r.data.length > 0, additional: { page: page + 1 } };
  };

  // ── RENDER ────────────────────────────────────────────────

  render() {
    const token = localStorage.usertoken, decoded = jwt_decode(token);

    // Derived counters (no state, no extra renders)
    const sku_monitor = Object.values(this.state.sku_list || {}).filter(Boolean).length;
    const sch_monitor = Object.values(this.state.scheme_master_list || {}).filter(Boolean).length;

    const customStyles = {
  control: (provided) => ({
    ...provided,
    padding: "0px",
    minHeight: "30px",  // Reduced from 32px
    margin: "0px",
    fontSize: "12px",    // Add this
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "20px",      // Reduced from 23px
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "2px",      // Reduced from 5px
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "4px",      // Reduced from 15px
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0px 4px',  // Reduced padding
    fontSize: "12px",    // Add this
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 999,
    fontSize: "12px",    // Add this
  }),
  option: (provided) => ({
    ...provided,
    fontSize: "12px",    // Add this
    padding: "6px 10px", // Reduced padding
  }),
  multiValue: (provided) => ({
    ...provided,
    fontSize: "11px",    // Add this
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: "11px",    // Add this
    padding: "2px 4px",  // Reduced padding
  }),
};

    const MenuList = ({ children }) => {
      const rows = children, count = rows.length || 0, height = count < 9 ? 40 * count : 300;
      return <List style={{ width: '100%' }} width={300} height={height} rowHeight={30} rowCount={count} rowRenderer={({ key, index, style }) => <div key={key} style={style}>{rows[index]}</div>} />;
    };

    // ── SKU table: filter → paginate → memoized rows ─────
    const skuFiltered = filterSku(this.state.raw_display_data, this.state.skuSearch);
    const skuTotalPages = Math.max(1, Math.ceil(skuFiltered.length / this.state.skuPageSize));
    const skuSafePg = Math.min(this.state.skuPage, skuTotalPages);
    const skuSlice = skuFiltered.slice((skuSafePg - 1) * this.state.skuPageSize, skuSafePg * this.state.skuPageSize);

    const sku_show_data = skuSlice.map((v, i) => (
      <SkuRow key={v.brandSku} value={v} rowIndex={(skuSafePg - 1) * this.state.skuPageSize + i}
        isChecked={!!this.state.sku_list[v.brandSku]} onToggle={this._boundChangeSKU} />
    ));

    // ── Scheme table: filter → paginate → memoized rows ──
    const schFiltered = filterScheme(this.state.scheme_master_data, this.state.schSearch);
    const schTotalPages = Math.max(1, Math.ceil(schFiltered.length / this.state.schPageSize));
    const schSafePg = Math.min(this.state.schPage, schTotalPages);
    const schSlice = schFiltered.slice((schSafePg - 1) * this.state.schPageSize, schSafePg * this.state.schPageSize);

    const scheme_data_master = schSlice.map(v => {
      const t = new Date(v.recordUpdateTimestamp);
      t.setHours(t.getHours() + 5); t.setMinutes(t.getMinutes() + 30);
      return <SchemeRow key={v.id} value={v} ts={t.toJSON().slice(0, 19).replace('T', ' ')}
        isChecked={!!this.state.scheme_master_list[v.id]} onToggle={this._boundChangeScheme} />;
    });

    const tabVisible = id => this._visitedTabs.has(id) || this.state.current_tab === id;

    const bank_names = this.state.bank_name_list.map((value, index) => {
      if (this.state.activer[index] !== true) return null;
      const maxDis = this.state.brand_subvention_list[index] !== "Percent";
      return (
        <div className="col-md-3 col-sm-6 col-xs-12" key={index}>
          <div className="card p-5">
            <h5>{value}</h5>
            <Checkbox number={`${index}_${value}`} label="EMI Scheme Applicable?" onChange={this.changeEMIstatus.bind(this, index)} checked={!!this.state.isEMI[index]} />
            <Checkbox number={`zero_cost_${index}`} label="Zero Cost EMI?" onChange={this.changeZeroCoststatus.bind(this, index)} disabled={!this.state.isEMI[index]} checked={!!this.state.zeroCost[index]} />
            <div className="row">
              {['3', '6', '9', '12', '18', '24'].map(m => (
                <div key={m} className="col-md-6 col-sm-12">
                  <Checkbox number={`${m}_months_${index}`} label={`${m} months`} onChange={this.changeTenure.bind(this, index, `M${m}`)} disabled={!this.state.isEMI[index]} checked={!!this.state.bankOptions[index][`M${m}`]} />
                </div>
              ))}
            </div>
            <Label className="labela">Brand Subvention</Label>
            <div className="row m-0 bottompaddingsubvention">
              <div className="col-xs-8 pr-0">
                <Select isClearable={false} options={this.state.brandSubventionOptions} defaultValue={this.state.brandSubventionOptions[0]} placeholder="Percent" onChange={this.updateBrandSubvention.bind(this, index)} />
              </div>
              <div className="col-xs-4 pl-0">
                <Input className="form-control-subvention" value={this.state.brand_subvention_value[index]} onChange={e => { const v = e.target.value; if ((v * 1) < 0 || v === '') this.handleNotificationClick("tr", 3, "Insert value greater than 0"); this.setState({ brand_subvention_value: { ...this.state.brand_subvention_value, [index]: v }, mapField2: false }); }} />
              </div>
            </div>
            <Label className="labela">Maximum Cashback</Label>
            <div className="row m-0">
              <div className="col-xs-8 pl-0">
                <Input className="form-control-subvention" disabled={maxDis} value={this.state.maximum_cashback[index]} onChange={e => { const v = e.target.value; if ((v * 1) < 0 || v === '') this.handleNotificationClick("tr", 3, "Insert value greater than 0"); this.setState({ maximum_cashback: { ...this.state.maximum_cashback, [index]: v }, mapField2: false }); }} />
              </div>
            </div>
            <div className="row m-0">
              <Checkbox number={`isUpfront_${index}`} label="Cashback is upfront?" onChange={this.changeisUpfrontstatus.bind(this, index)} checked={!!this.state.isUpfront[index]} />
            </div>
          </div>
        </div>
      );
    });

    const map_scheme = this.state.adderArray.map((_, index) => {
      const isLast = index === this.state.adderArray.length - 1, isFirst = index === 0;
      return (
        <div key={index} className="row top-margin-30">
          <div   className="col-md-4">
            <ReactMultiSelectCheckboxes components={{ MenuList }} isClearable={false} onChange={this.changeSchemeMap.bind(this, index)} styles={customStyles} options={this.state.scheme_list_name} isMulti placeholderButtonLabel="All" />
          </div>
          <div   className="col-md-4">
            <ReactMultiSelectCheckboxes components={{ MenuList }} isClearable={false} onChange={this.changeSkuMap.bind(this, index)} styles={customStyles} options={this.state.model_list_name} isMulti placeholderButtonLabel="All" />
          </div>
          <div className="col-md-4">
            {isLast && <Link className="viewButton" onClick={this.addArray.bind(this)}><span className="glyphicon glyphicon-plus" /></Link>}
            {isLast && !isFirst && <Link className="viewButton" onClick={this.popArray.bind(this)}><span className="glyphicon glyphicon-minus" /></Link>}
          </div>
        </div>
      );
    });

    const merchant_displayer = () => {
      if (decoded.userGroup == 2 || decoded.userGroup == '2') return <div />;
      return (
        <div className="row">
          {[
            { label: 'Select Merchant:', opts: this.state.merchantList, val: this.state.merchantValue, fn: this.changeMerchant1.bind(this) },
            { label: 'Select Chain:', opts: this.state.chainList, val: this.state.chainValue, fn: this.changeChain.bind(this) },
            { label: 'Select Store:', opts: this.state.storeList, val: this.state.storeValue, fn: this.changeStore.bind(this) },
          ].map(({ label, opts, val, fn }) => (
            <div key={label} className="col-md-4">
              <Label>{label}</Label>
              <div  >
              <ReactMultiSelectCheckboxes components={{ MenuList }} isClearable={false} onChange={fn} styles={customStyles} options={opts} value={val} isMulti placeholderButtonLabel="All" />
            </div>
            </div>
          ))}
        </div>
      );
    };

    const NO_DATA_ROW = (cols, msg) => (
      <tr><td colSpan={cols} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>{msg}</td></tr>
    );

    return (
      <div className="content">
        <NotificationSystem ref="notificationSystem" style={style} />
        <ReactTooltip />
        <CommonNavbar />
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12 top-pad-5">
              <div className="card">
                <div className="header">
                  <h4 className="title">Create Promotion {this.state.party_name}</h4>
                </div>
                <div className="content">
                  <div className="sweet-loading">
                    <BeatLoader css={override} sizeUnit="px" size={20} color="#123abc" loading={this.state.loading} />
                  </div>
                  <Tabs defaultTab={this.state.current_tab} vertical className="vertical-tabs">
                    <TabList>
                      <Tab className="tab-title" tabFor="vertical-tab-zero" onClick={this.tabClicker.bind(this, 0)}><h4 className="title">Party</h4><span className="text-muted">Select party</span></Tab>
                      <Tab className="tab-title" tabFor="vertical-tab-one" onClick={this.tabClicker.bind(this, 1)} disabled={this.state.stepper[1]}><h4 className="title">Products (SKUs)</h4><span className="text-muted">Select products</span></Tab>
                      <Tab className="tab-title" tabFor="vertical-tab-two" onClick={this.tabClicker.bind(this, 2)} disabled={this.state.stepper[2]}><h4 className="title">Schemes</h4><span className="text-muted">Select schemes</span></Tab>
                      <Tab className="tab-title" tabFor="vertical-tab-three" onClick={this.tabClicker.bind(this, 3)} disabled={this.state.stepper[3]}><h4 className="title">Map schemes</h4><span className="text-muted">Map schemes</span></Tab>
                      <Tab className="tab-title" tabFor="vertical-tab-four" onClick={this.tabClicker.bind(this, 4)} disabled={this.state.stepper[4]}><h4 className="title">Merchants</h4><span className="text-muted">Select merchants</span></Tab>
                      <Tab className="tab-title" tabFor="vertical-tab-five" onClick={this.tabClicker.bind(this, 5)} disabled={this.state.stepper[5]}><h4 className="title">Details</h4><span className="text-muted">Add details</span></Tab>
                    </TabList>

                    {/* TAB 0 — Party */}
                    <TabPanel tabId="vertical-tab-zero">
                      <div className="col-md-12"><div className="card noshadow">
                        <div className="header header-promotion">
                          <h4 className="title">Party</h4>
                          <span className="text-muted">Please select a party for whom a promotion is to be created</span>
                        </div>
                        <div className="row top-margin-10">
                          <div className="col-md-4">
                            <Label>Select party type</Label>
                            <Select name="party_type" value={this.state.selected_party_type2} onChange={this.updatePartyType.bind(this)} options={this.state.party_options} placeholder="Select party type" isDisabled={this.state.enableDisablePartyType} />
                          </div>
                          <div className="col-md-4">
                            <Label>{this.state.selected_party_type === "Brand" ? "Select brand" : "Select party"}</Label>
                            <Select name="party_name" value={this.state.selected_party2} onChange={this.updatePartyValue.bind(this)} options={this.state.party_list} placeholder="Select party" isDisabled={this.state.enableDisablePartyType} />
                          </div>
                        </div>
                        <div className="row top-margin-30"><div className="col-md-12">
                          <button disabled={this.state.select_product_button} onClick={this.openProducts.bind(this, true)} className="btn btn-primary pull-right btn-capitalize">Select Products</button>
                        </div></div>
                      </div></div>
                    </TabPanel>

                    {/* TAB 1 — SKUs */}
                    <TabPanel tabId="vertical-tab-one">
                      {tabVisible('vertical-tab-one') && (
                        <div className="card-query-builder"><div className="col-md-12">
                          <div className="card noshadow" ref={this.myRef2}>
                            <div className="header header-promotion">
                              <h4 className="title">Products (SKUs)</h4>
                              <span className="text-muted">Please select products/SKUs from the below given list</span>
                            </div>

                            <Modal className="modalsmall" isOpen={this.state.newConfirmationModel} toggle={this.toggleConfirmationModel.bind(this)} size="sm">
                              <ModalBody className="smallcontent"><div className="row"><div className="col-xs-12"><h4 className="modalh4">Are you sure you want to save?</h4></div></div></ModalBody>
                              <ModalFooter>
                                <Button color="primary" disabled={this.state.create_button_disabler} className="btn-capitalize" onClick={this.sendCompleteScheme.bind(this)}>Yes</Button>{' '}
                                <Button color="secondary" className="btn-capitalize2" onClick={this.toggleConfirmationModel.bind(this)}>No</Button>
                              </ModalFooter>
                            </Modal>

                            <div className="col-md-12">
                              <div className="pull-right">
                                <a href="#" onClick={this.selectAll.bind(this)}>Select all</a>&nbsp;&nbsp;&nbsp;
                                <a href="#" onClick={this.deSelectAll.bind(this)}>Clear</a>
                              </div>
                            </div>

                            {/* Search bar + page-size selector */}
                            <TableControls
                              searchValue={this.state.skuSearchRaw}
                              onSearch={this.handleSkuSearch}
                              pageSize={this.state.skuPageSize}
                              onPageSize={this.handleSkuPageSize}
                            />

                            <div className="table-full-width table-responsive">
                              <table className="table table-striped table-hover">
                                <thead>
                                  <tr>
                                    <th>Sr No.</th><th>Brand</th><th>Category</th><th>Model Name</th>
                                    <th>Model Number</th><th>Model Description</th><th>SKU</th>
                                    <th>MRP</th><th>MOP</th><th>Applicable?</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sku_show_data.length > 0
                                    ? sku_show_data
                                    : NO_DATA_ROW(10, this.state.skuSearch ? `No results for "${this.state.skuSearch}"` : 'No data available')
                                  }
                                </tbody>
                              </table>
                            </div>

                            {/* Info text + page buttons */}
                            <TableFooter
                              page={skuSafePg} totalPages={skuTotalPages}
                              totalRows={this.state.raw_display_data.length}
                              filteredRows={skuFiltered.length}
                              pageSize={this.state.skuPageSize}
                              onPage={this.handleSkuPage}
                            />

                            <div className="col-md-12" style={{ marginTop: 10 }}>
                              <button onClick={this.openSectionToBanks.bind(this, true)} className="btn btn-primary pull-right btn-capitalize">Choose Schemes</button>
                            </div>
                          </div>
                        </div></div>
                      )}
                    </TabPanel>

                    {/* TAB 2 — Schemes */}
                    <TabPanel tabId="vertical-tab-two">
                      {tabVisible('vertical-tab-two') && (
                        <div className="card-query-builder"><div className="col-md-12">
                          <div className="card noshadow" ref={this.myRef2}>
                            <div className="header header-promotion">
                              <h4 className="title">Schemes</h4>
                              <span className="text-muted">Please select schemes from below list</span>
                            </div>

                            <div className="col-md-12">
                              <div className="pull-right">
                                <a href="#" onClick={this.selectAllPromotions.bind(this)}>Select all</a>&nbsp;&nbsp;&nbsp;
                                <a href="#" onClick={this.deselectAllPromotions.bind(this)}>Clear</a>
                              </div>
                            </div>

                            {/* Search bar + page-size selector */}
                            <TableControls
                              searchValue={this.state.schSearchRaw}
                              onSearch={this.handleSchSearch}
                              pageSize={this.state.schPageSize}
                              onPageSize={this.handleSchPageSize}
                            />

                            <div className="table-full-width table-responsive">
                              <table className="table table-striped table-hover">
                                <thead>
                                  <tr>
                                    <th>Scheme Name</th><th>Bank Name</th><th>Scheme Type</th><th>EMI Type</th>
                                    <th>Tenure</th><th>Avail Cashback?</th><th>Upfront CB Brand</th>
                                    <th>Upfront CB Bank</th><th>Post CB Brand</th><th>Post CB Bank</th>
                                    <th>Low Cost Subvention</th><th>Upfront Brand Sub</th><th>Upfront Brand Sub Amt</th>
                                    <th>Upfront Max CB</th><th>Post Brand Sub</th><th>Post Brand Sub Amt</th>
                                    <th>Post Max CB</th><th>Bank CB %</th><th>Brand CB Max</th><th>TID</th>
                                    <th>Timestamp</th><th>Select</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {scheme_data_master.length > 0
                                    ? scheme_data_master
                                    : NO_DATA_ROW(21, this.state.schSearch ? `No results for "${this.state.schSearch}"` : 'No data available')
                                  }
                                </tbody>
                              </table>
                            </div>

                            {/* Info text + page buttons */}
                            <TableFooter
                              page={schSafePg} totalPages={schTotalPages}
                              totalRows={this.state.scheme_master_data.length}
                              filteredRows={schFiltered.length}
                              pageSize={this.state.schPageSize}
                              onPage={this.handleSchPage}
                            />

                            <div className="row" style={{ marginTop: 10 }}>
                              <div className="col-md-12">
                                <button onClick={this.sendEMIMappings.bind(this, true)} className="btn btn-primary pull-right btn-capitalize">Map schemes</button>
                              </div>
                            </div>
                          </div>
                        </div></div>
                      )}
                    </TabPanel>

                    {/* TAB 3 — Map Schemes */}
                    <TabPanel tabId="vertical-tab-three">
                      {tabVisible('vertical-tab-three') && (
                        <div className="col-md-12"><div className="card noshadow">
                          <div className="header header-promotion">
                            <h4 className="title">Map schemes</h4>
                            <span className="text-muted">Map schemes to products you want to link with</span>
                          </div>
                          <div className="row"><div className="col-md-4">Scheme Name:</div><div className="col-md-4">Applicable Models:</div></div>
                          {map_scheme}
                          <div className="row top-margin-30"><div className="col-md-12">
                            <button disabled={this.state.select_product_button} onClick={this.mapSchemesToProducts.bind(this)} className="btn btn-primary pull-right btn-capitalize">Choose Merchants</button>
                          </div></div>
                        </div></div>
                      )}
                    </TabPanel>

                    {/* TAB 4 — Merchants */}
                    <TabPanel tabId="vertical-tab-four">
                      {tabVisible('vertical-tab-four') && (
                        <div className="col-md-12"><div className="card noshadow">
                          <div className="row"><div className="col-md-12">
                            <div className="header header-promotion">
                              <h4 className="title">Merchants</h4>
                              <span className="text-muted">Please select merchants from below list</span>
                            </div>
                            {merchant_displayer()}
                            <div className="row top-margin-10"><div className="col-md-12">
                              <button onClick={this.merchantSubmitHandler.bind(this)} className="btn btn-primary pull-right btn-capitalize">Continue</button>
                            </div></div>
                          </div></div>
                        </div></div>
                      )}
                    </TabPanel>

                    {/* TAB 5 — Details */}
                    <TabPanel tabId="vertical-tab-five">
                      {tabVisible('vertical-tab-five') && (
                        <div className="card-query-builder subvention-brand"><div className="col-md-12">
                          <div className="card noshadow" ref={this.myRef}>
                            <div className="header header-promotion">
                              <h4 className="title">Promotion Details</h4>
                              <span className="text-muted">Please provide the following promotion details</span>
                            </div>
                            <div className="content">
                              <div className="row bottompaddingsubvention">
                                <div className="col-md-12">
                                  <div className="alert alert-info">
                                    <strong>Suffix to be added:</strong> {this.state.suffixPreview || '(No suffix will be added)'}
                                    <br /><small>Based on your selections above, this suffix will be automatically appended to the promotion name.</small>
                                  </div>
                                </div>
                              </div>
                              <div className="row bottompaddingsubvention">
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>Segment</Label>
                                  <Select name="segment" value={this.state.selectedSegment} onChange={this.handleSegmentChange} options={this.state.segmentOptions} placeholder="Select Segment(s)" isMulti styles={customStyles} />
                                  <label className="mt-2">Segment Suffix type</label>
                                  <Select name="segmentSuffixType" value={this.state.segmentSuffixType ? { value: this.state.segmentSuffixType, label: this.state.segmentSuffixType === 'EXCLUDE' ? 'Exclude' : 'Include' } : undefined} onChange={this.handleSegmentSuffixTypeChange} options={[{ value: 'EXCLUDE', label: 'Exclude' }, { value: 'INCLUDE_ONLY', label: 'Include' }]} styles={customStyles} />
                                </div>
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>State</Label>
                                  <Select name="state" value={this.state.selectedState} onChange={this.handleStateChange} options={this.state.stateOptions} placeholder="Select State(s)" isMulti styles={customStyles} />
                                  <Label className="mt-2">State Suffix Type</Label>
                                  <Select name="stateSuffixType" value={this.state.stateSuffixType ? { value: this.state.stateSuffixType, label: this.state.stateSuffixType === 'EXCLUDE' ? 'Exclude' : 'Include' } : undefined} onChange={this.handleStateSuffixTypeChange} options={[{ value: 'EXCLUDE', label: 'Exclude' }, { value: 'INCLUDE_ONLY', label: 'Include' }]} styles={customStyles} />
                                </div>
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>City</Label>
                                  <Select name="city" value={this.state.selectedCity} onChange={this.handleCityChange} options={this.state.cityOptions} placeholder={this.state.selectedState.length > 0 ? "Select City" : "Select State first"} isDisabled={this.state.selectedState.length === 0} isMulti styles={customStyles} />
                                  <Label className="mt-2">City Suffix Type</Label>
                                  <Select name="citySuffixType" value={{ value: this.state.citySuffixType, label: this.state.citySuffixType === 'EXCLUDE' ? 'Exclude' : 'Include' }} onChange={this.handleCitySuffixTypeChange} options={[{ value: 'EXCLUDE', label: 'Exclude' }, { value: 'INCLUDE_ONLY', label: 'Include' }]} styles={customStyles} />
                                </div>
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>Promotion Name</Label>
                                  <Input id="campaign_name" className="maxheighter" value={this.state.campaign_name || ''} placeholder="Promotion name will be auto-generated" readOnly />
                                </div>
                                <div className="col-sm-12 col-md-6 col-xs-12">
                                  <Label>Promotion Description</Label>
                                  <Input id="campaign_desc" className="maxheighter" value={this.state.campaign_desc} onChange={e => { const v = e.target.value; if (!v) this.handleNotificationClick("tr", 3, "Insert promotion description"); this.setState({ campaign_desc: v, mapField2: false }); }} />
                                </div>
                              </div>
                              <div className="row bottompaddingsubvention">
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>Select start date</Label>
                                  <DatePicker dateFormat="dd-MM-yyyy" selected={Date.parse(this.state.startDate)} onChange={this.handleFromChange} minDate={new Date()} />
                                </div>
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>Select end date</Label>
                                  <DatePicker dateFormat="dd-MM-yyyy" selected={this.state.endDate ? new Date(this.state.endDate) : null} onChange={this.handleToChange} minDate={new Date()} />
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-sm-6 col-md-3 col-xs-12">
                                  <Label>Select EMI Type</Label>
                                  <Select name="emi_type" value={this.state.selceted_emi_type_value_label} onChange={this.updateEmiTypeValue.bind(this)} options={this.state.selected_emi_type} placeholder="Select Emi" />
                                </div>
                              </div>
                              <br />
                              <div className="row"><div className="col-md-12">
                                <button onClick={this.openSectionToProduct.bind(this, true)} className="btn btn-primary pull-right btn-capitalize">Create Promotion</button>
                              </div></div>
                            </div>
                          </div>
                        </div></div>
                      )}
                    </TabPanel>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AdminCreatePromotion1;